import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "components/ui/modals";
import { formatDateAndTime } from "utils/date";
import { ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";
import {
  EyeIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import DeletePaymentRecon from "components/paymentrecon/DeletePaymentRecon";
import PaymentReconReportModal from "components/paymentrecon/PaymentReconReportModal";
import { useAllPaymentReconMains } from "http-hooks/paymentreconmain/paymentreconmain";

const columns = [
  { key: "reconid", header: "Recon ID" },
  {
    key: "recdate",
    header: "Date",
    render: (item) => formatDateAndTime(item.recdate),
  },
  {
    key: "custid",
    header: "Customer",
    render: (item) =>
      item.customer ? (
        <div className="font-medium">{item.customer.name}</div>
      ) : (
        item.custid || "-"
      ),
  },
  {
    key: "vendid",
    header: "Vendor",
    render: (item) =>
      item.vendor ? (
        <div className="font-medium">{item.vendor.name}</div>
      ) : (
        item.vendid || "-"
      ),
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function PaymentReconList() {
  const navigate = useNavigate();
  const [printItemId, setPrintItemId] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [searchRecon, setSearchRecon] = useState("");

  const [filter, setFilter] = useState({
    reconid: "",
    page: 1,
    limit: 10,
  });

  const {
    data: { data: allRecons, page: currentPage, totalPages } = {},
  } = useAllPaymentReconMains(filter);

  const handleNew = () => {
    navigate("/paymentrecon/new");
  };

  const handleEdit = (id) => {
    navigate(`/paymentrecon/${encodeURIComponent(id)}`);
  };

  const handlePrint = (id) => {
    setPrintItemId(id);
  };

  const handleReconSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, reconid: searchTerm, page: 1 }));
  };

  const clearFilters = () => {
    setSearchRecon("");
    setFilter({ reconid: "", page: 1, limit: 10 });
  };

  return (
    <MasterPage
      title="Payment Reconciliation"
      headerActions={
        <>
          <AddButton
            onClick={() => setShowReportModal(true)}
            variant="outline"
          >
            <DocumentChartBarIcon className="h-5 w-5" />
            Report
          </AddButton>
          <AddButton onClick={handleNew}>New Payment Recon</AddButton>
        </>
      }
      filters={
        <FilterRow>
          <FilterCell>
            <InputSearch
              value={searchRecon}
              onChange={(e) => setSearchRecon(e.target.value)}
              onSearch={handleReconSearch}
              label="Recon ID"
              placeholder="Search by Recon ID"
            />
          </FilterCell>
          <ClearFiltersButton onClick={clearFilters} />
        </FilterRow>
      }
      columns={columns}
      data={allRecons}
      rowKey="reconid"
      deleteComponent={DeletePaymentRecon}
      extraModals={
        <>
          {printItemId && (
            <Modal
              open={!!printItemId}
              setOpen={(val) => !val && setPrintItemId(null)}
            >
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-4">
                  Print Payment Reconciliation
                </h3>
                <p>Print functionality will be implemented here</p>
                <button
                  onClick={() => setPrintItemId(null)}
                  className="mt-4 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white"
                >
                  Close
                </button>
              </div>
            </Modal>
          )}
          {showReportModal && (
            <Modal
              open={showReportModal}
              setOpen={setShowReportModal}
              maxWidth="lg"
            >
              <PaymentReconReportModal
                onClose={() => setShowReportModal(false)}
              />
            </Modal>
          )}
        </>
      }
      renderActions={(item, setDeleteItemId) => (
        <>
          <ActionButton
            onClick={() => handlePrint(item.reconid)}
            variant="primary"
            title="View/Print"
          >
            <EyeIcon className="h-4 w-4" />
            <span className="sr-only">View/Print, {item.reconid}</span>
          </ActionButton>
          <EditAction
            onClick={() => handleEdit(item.reconid)}
            title="Edit"
          />
          <DeleteAction
            onClick={() => setDeleteItemId(item.reconid)}
            title="Delete"
          />
        </>
      )}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
